import type { FieldSortKey, SortDirectionKey } from "@/base/Sorting"
import type { Reducer } from "@reduxjs/toolkit"

import * as AppStorage from "@/base/async-storage"
import {
  activateFieldsAsync,
  archiveFieldsAsync,
  createFieldAsync,
  deleteFieldAsync,
  renameFieldAsync,
  setFieldRowDirectionAsync,
  updateFieldBoundaryAsync,
} from "@/base/farmhq-api"
import * as Geo from "@/base/geo"
import { loadActiveFarmAsync } from "@/base/load-app"
import { makePrioritySort } from "@/base/sort-priority"
import * as Models from "@/redux/models"
import { createAction, createSlice, isAnyOf } from "@reduxjs/toolkit"
import * as turf from "@turf/turf"
import i18next from "i18next"
import _ from "lodash"
import { createCachedSelector } from "re-reselect"
import { createSelector } from "reselect"

import type { RootState } from "./root.reducer"

import type { SortComparator } from "@/base/types"
import { setFieldActionsName } from "./actions"
import { signOutAsync } from "./auth.reducer"
import { deselectItem } from "./status-map.reducer"

export type FieldType = "center_pivot" | "polygon"

export const CREATE_FIELD_STAGES = [
  "setFieldBoundary",
  "setFieldName",
  "setFieldLabelRotation",
  "setFieldRowDirection",
] as const
export type CreateFieldStage = (typeof CREATE_FIELD_STAGES)[number]
export type FieldStatusAction = "activateField" | "archiveField"
export type FieldActionName =
  | CreateFieldStage
  | FieldStatusAction
  | "deleteField"
  | "fieldIrrigationHistory"

const { adapter, selectAll } = Models.farmField
export const cancelFieldAction = createAction("fields/cancelFieldAction")

interface FieldsState extends Models.ModelState<Models.FarmField> {
  actionName?: FieldActionName
  sortDirection: SortDirectionKey
  sortKey: FieldSortKey
}

const INITIAL_STATE: FieldsState = {
  entities: {},
  ids: [],
  sortDirection: AppStorage.STORAGE_DEFAULTS.fieldSortDirection,
  sortKey: AppStorage.STORAGE_DEFAULTS.fieldSortKey,
}
const slice = createSlice({
  extraReducers: (builder) => {
    return builder
      .addCase(setFieldActionsName, (state, action) => {
        if (typeof action.payload === "string") {
          state.actionName = action.payload
        } else if (action.payload) {
          state.actionName = action.payload.value
        } else {
          state.actionName = undefined
        }
      })
      .addCase(cancelFieldAction, (state) => {
        state.actionName = undefined
      })
      .addCase(AppStorage.setFieldSortDirection.pending, (state, action) => {
        state.sortDirection = action.meta.arg
      })
      .addCase(AppStorage.setFieldSortKey.pending, (state, action) => {
        state.sortKey = action.meta.arg
      })
      .addCase(
        AppStorage.readLocalStorageAsync.fulfilled,
        (state, { payload }) => {
          state.sortDirection = payload.fieldSortDirection
          state.sortKey = payload.fieldSortKey
        },
      )
      .addCase(loadActiveFarmAsync.fulfilled, (state, action) => {
        adapter.setAll(
          state,
          // We don't want to show center pivot fields in the field list
          // because we don't use them and it is confusing for users
          (action.payload.fields ?? []).filter((field) => {
            if (field.fieldType === "center_pivot") {
              return false
            }
            return true
          }),
        )
      })
      .addCase(createFieldAsync.fulfilled, (state, { payload }) => {
        adapter.upsertOne(state, Models.prepareField(payload))
      })
      .addCase(renameFieldAsync.fulfilled, (state, action) => {
        adapter.updateOne(state, {
          changes: {
            fieldName: action.meta.arg.fieldName,
            labelRotationDegrees: action.meta.arg.labelRotationDegrees,
          },
          id: action.meta.arg.fieldId,
        })
      })
      .addCase(setFieldRowDirectionAsync.fulfilled, (state, { meta }) => {
        adapter.updateOne(state, {
          changes: { rowDirectionAzimuthDegrees: meta.arg.value },
          id: meta.arg.fieldId,
        })
      })
      .addCase(updateFieldBoundaryAsync.fulfilled, (state, { meta }) => {
        adapter.updateOne(state, {
          changes: { polygon: meta.arg.polygon },
          id: meta.arg.fieldId,
        })
      })
      .addCase(archiveFieldsAsync.fulfilled, (state, { meta }) => {
        adapter.updateMany(
          state,
          meta.arg.ids.map((id) => ({ changes: { isActive: false }, id })),
        )
      })
      .addCase(activateFieldsAsync.fulfilled, (state, { meta }) => {
        adapter.updateMany(
          state,
          meta.arg.ids.map((id) => ({ changes: { isActive: true }, id })),
        )
      })
      .addCase(deleteFieldAsync.fulfilled, (state, { meta }) => {
        adapter.removeOne(state, meta.arg.fieldId)
      })
      .addMatcher(isAnyOf(signOutAsync.fulfilled), (state) => {
        adapter.removeAll(state)
      })
      .addMatcher(
        isAnyOf(
          archiveFieldsAsync.fulfilled,
          activateFieldsAsync.fulfilled,
          updateFieldBoundaryAsync.fulfilled,
          deleteFieldAsync.fulfilled,
          deselectItem,
        ),
        (state) => {
          state.actionName = undefined
        },
      )
  },
  initialState: INITIAL_STATE,
  name: "farmFields",
  reducers: {},
})

const farmFields: Reducer<FieldsState> = slice.reducer
export default farmFields

export function calculateFieldArrowLength(
  polygon: Geo.PolygonGeoJson,
): number | undefined {
  if (typeof polygon === "undefined") {
    throw new TypeError("Cannot convert polygon")
  }
  const center = Geo.polygon(polygon)?.getCenter()?.coordinates
  if (!center) {
    return undefined
  }

  const tangents = turf.polygonTangents(center, polygon)
  const longest = _.max(
    tangents.features.map((point) => {
      const coordinates = point.geometry.coordinates
      return turf.distance(coordinates, center, { units: "millimeters" })
    }),
  )
  return longest
}
export const getActiveFieldsFromStateCached = createSelector(
  selectAll,
  function getActiveFieldFromState(fields) {
    return fields.filter((field): boolean => field.isActive)
  },
)

// export const getArchivedFieldIdsFromStateCached = createSelector(
//   selectAll,
//   (fields) => {
//     return fields
//       .filter((field): boolean => !field.isActive)
//       .map((field) => field.id)
//   },
// )
// export const getNumArchivedFieldsFromState = createSelector(
//   getArchivedFieldIdsFromStateCached,
//   (fields) => fields.length,
// )

// export const getNumActiveFieldsFromState = createSelector(
//   selectTotal,
//   getNumArchivedFieldsFromState,
//   (total, count) => total - count,
// )

export interface FieldListItemType extends Models.FarmField {
  areaText: string | undefined
  index: number
}
export function getFieldActionNameFromState(
  state: RootState,
): FieldActionName | undefined {
  return state.farmFields.actionName
}

export function getFieldSortKeyFromState(state: RootState): FieldSortKey {
  return state.farmFields.sortKey
}

export function getFieldSortDirectionFromState(
  state: RootState,
): SortDirectionKey {
  return state.farmFields.sortDirection
}

const sortFieldByName: SortComparator<FieldListItemType> = (a, b) => {
  const nameA = a?.fieldName ?? ""
  const nameB = b?.fieldName ?? ""
  return nameA.localeCompare(nameB)
}

/**
 * Separate active and archived fields
 */
const sortByFieldStatus: SortComparator<FieldListItemType> = (a, b) => {
  const isActiveB = b?.isActive === true
  const isActiveA = a?.isActive === true
  if (isActiveB && !isActiveA) {
    return -1
  }
  if (!isActiveB && isActiveA) {
    return 1
  }
  return 0
}

function makeFieldsSorter(
  sortKey: FieldSortKey,
  sortDirection: SortDirectionKey,
): ((a: FieldListItemType, b: FieldListItemType) => number) | undefined {
  return (a, b): number => {
    let sorter: SortComparator<typeof a>
    switch (sortKey) {
      case "alphabetical": {
        sorter = makePrioritySort(
          sortDirection,
          sortFieldByName,
          sortByFieldStatus,
        )
        break
      }
      case "field_status": {
        sorter = makePrioritySort(
          sortDirection,
          sortByFieldStatus,
          sortFieldByName,
        )
        break
      }
    }
    return sorter(a, b)
  }
}

export const getFieldListItemsFromStateSorted: (
  state: RootState,
) => FieldListItemType[] = createCachedSelector(
  selectAll,
  getFieldSortKeyFromState,
  getFieldSortDirectionFromState,
  (fields, sortKey, sortDirection): FieldListItemType[] => {
    return fields
      .filter((field) => {
        // This is a deprecated type of field...we now handle this
        // in the device configuration
        if (field.fieldType === "center_pivot") {
          return false
        }
        return true
      })
      .map((field, index) => {
        const archivedText = i18next.t("archived")
        return {
          ...field,
          fieldName: `${field.fieldName}${
            field.isActive && typeof archivedText === "string"
              ? ""
              : `(${archivedText})`
          } `,
          index,
          svgInfo: Models.calculateFieldSvgPath(field),
        }
      })
      .sort(makeFieldsSorter(sortKey, sortDirection))
  },
)((state: RootState) => {
  const sortKey = getFieldSortKeyFromState(state)
  const direction = getFieldSortDirectionFromState(state)
  return `fields/${sortKey}/${direction}`
})
